import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import { TOKENS, fmtMoney, ticketGameColor } from '../theme';

// Spec §7 — adaptive horizontal layout. Each machine sits in its own
// rounded card; tickets flow inline with amount above + time below.
// Hero card mirrors the lavender-tinted style from the reference screenshot.
export default function TicketOutDetail({ sessionData }) {
  const ticketOuts = sessionData?.ticketOuts || [];

  const machines = useMemo(() => {
    const byMachine = {};
    ticketOuts.forEach((t) => {
      if (t.status === 'deleted') return;
      const key = t.machine;
      if (!byMachine[key]) {
        byMachine[key] = {
          machine: key,
          tickets: [],
          // The backend joins AssignMachineToGame to attach `game`
          // to every ticket. Older payloads may have it on the
          // first ticket only; default to 'OTHER' last.
          game: t.game || 'OTHER',
        };
      }
      // If the first ticket of a machine lacked a game but a later
      // one has it (rare), upgrade once.
      if (byMachine[key].game === 'OTHER' && t.game)
        byMachine[key].game = t.game;
      byMachine[key].tickets.push({
        amt: Number(t.ticketOut || 0),
        time: t.time,
        modified: t.modified,
      });
    });
    return Object.values(byMachine)
      .map((m) => ({ ...m, total: m.tickets.reduce((s, t) => s + t.amt, 0) }))
      .sort((a, b) => b.total - a.total);
  }, [ticketOuts]);

  const allAmounts = ticketOuts.map((t) => Number(t.ticketOut || 0));
  const total = allAmounts.reduce((s, n) => s + n, 0);
  const biggest = allAmounts.length ? Math.max(...allAmounts) : 0;
  const biggestMachine = machines.find((m) =>
    m.tickets.some((t) => t.amt === biggest)
  )?.machine;

  const gameTotals = {};
  machines.forEach((m) => {
    gameTotals[m.game] = (gameTotals[m.game] || 0) + m.total;
  });
  const games = Object.entries(gameTotals)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {/* Hero card — lavender tint per reference screenshot */}
      <Box
        sx={{
          background: '#EDE9FE', // distinct lavender — more visible than violet50
          border: `1px solid ${TOKENS.violet200}`,
          borderRadius: 2,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.violet700,
            }}
          >
            Total Ticket Out
          </Typography>
          <Typography
            sx={{
              fontSize: 36,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: TOKENS.slate900,
              mt: 0.5,
              lineHeight: 1.1,
            }}
          >
            {fmtMoney(total)}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: TOKENS.violet700,
              mt: 0.75,
              fontWeight: 500,
            }}
          >
            {ticketOuts.length} tickets · {machines.length} machines · Highest{' '}
            {fmtMoney(biggest)}
            {biggestMachine && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  background: TOKENS.slate700,
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                M{biggestMachine}
              </Box>
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#DDD6FE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ConfirmationNumberIcon
            sx={{ color: TOKENS.violet700, fontSize: 28 }}
          />
        </Box>
      </Box>

      {/* Game breakdown — each game gets its own light-tinted card */}
      {games.length > 0 && (
        <Box
          sx={{
            background: '#fff',
            border: `1px solid ${TOKENS.slate200}`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
              mb: 1,
            }}
          >
            Breakdown by Game
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {games.map((g) => {
              const c = ticketGameColor(g.name);
              return (
                <Box
                  key={g.name}
                  sx={{
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 84,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: c.text,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {g.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: TOKENS.slate900,
                      mt: 0.25,
                    }}
                  >
                    {fmtMoney(g.amount)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Machine cards — one rounded card per machine, sorted by total desc */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {machines.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: TOKENS.slate400,
              fontSize: 13,
              background: '#fff',
              border: `1px solid ${TOKENS.slate200}`,
              borderRadius: 2,
            }}
          >
            No ticket-out activity for this session.
          </Box>
        )}
        {machines.map((m) => {
          const c = ticketGameColor(m.game);
          return (
            <Box
              key={m.machine}
              sx={{
                background: '#fff',
                border: `1px solid ${TOKENS.slate200}`,
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 17,
                      fontWeight: 700,
                      color: TOKENS.slate900,
                    }}
                  >
                    Machine {m.machine}
                  </Typography>
                  <Chip
                    size="small"
                    label={m.game}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      background: c.bg,
                      color: c.text,
                      border: `1px solid ${c.border}`,
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: TOKENS.blue700,
                      fontWeight: 600,
                    }}
                  >
                    {m.tickets.length}{' '}
                    {m.tickets.length === 1 ? 'ticket' : 'tickets'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: TOKENS.slate900,
                    }}
                  >
                    {fmtMoney(m.total)}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: 2.5,
                  rowGap: 1,
                }}
              >
                {m.tickets.map((t, ti) => (
                  <Box key={ti}>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: TOKENS.slate900,
                        lineHeight: 1.1,
                      }}
                    >
                      {fmtMoney(t.amt)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: TOKENS.slate500,
                        fontFamily: 'monospace',
                        lineHeight: 1.2,
                      }}
                    >
                      {t.time}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
